import { Button } from 'components/Base';
import { PlatformProviderChargeType } from 'components/ProviderPlatformCharges/types';
import TextInput from 'components/TextInput/TextInput';
import React, { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { formatCurrency } from 'utils';
import { chargeTypes } from '../../ProviderPlatformChargesComponent';
import ChargeTypeRadio from '../ChargeTypeRadio/ChargeTypeRadio';
import FamilyLinkRadio from '../FamilyLinkRadio/FamilyLinkRadio';
import FixedCharge from '../FixedCharge/FixedCharge';
import PolicyTypeRadio from '../PolicyTypeRadio/PolicyTypeRadio';
import SteppedCharge from '../SteppedCharge/SteppedCharge';
import TieredCharge from '../TieredCharge/TieredCharge';
import './chargeSet.scss';
import Tooltip from '../../../Base/Tooltip/Tooltip';

type ChargeSetProps = {
  index?: number;
  chargeData: PlatformProviderChargeType;
  updateCharge: Function;
  updateChargeData: Function;
  selectedPlatformType: any;
  selectedProviderType: any;
  applicationTypes: any;
  isFirmAdmin: boolean;
  platformIndex?: number;
};

const ChargeSet = ({
  chargeData,
  index = 0,
  updateCharge,
  updateChargeData,
  selectedPlatformType,
  selectedProviderType,
  applicationTypes,
  isFirmAdmin,
  platformIndex = 0
}: ChargeSetProps) => {
  const [currentPolicyTypes, setCurrentPolicyTypes] = useState(
    chargeData.policy_type
  );

  const [chargeType, setChargeType] = useState(chargeData.charge_type);

  const [isFamilyLinked, setIsFamilyLinked] = useState(
    chargeData.family_linked || false
  );
  const [aumRangeFrom, setAumRangeFrom] = useState(
    chargeData.aum_range.length === 2 && chargeData.aum_range[0] !== null
      ? formatCurrency(chargeData.aum_range[0].toString())
      : ''
  );
  const [aumRangeTo, setAumRangeTo] = useState(
    chargeData.aum_range.length === 2 && chargeData.aum_range[1] !== null
      ? formatCurrency(chargeData.aum_range[1].toString())
      : ''
  );
  const [aumRangeString, setAumRangeString] = useState('');

  const [formErrors, setFormErrors] = useState({} as any);

  const updatePolicyType = (policyType: number) => {
    const policyTypes = currentPolicyTypes;
    const indexOfId = policyTypes.indexOf(policyType);
    if (indexOfId === -1) {
      policyTypes.push(policyType);
      return setCurrentPolicyTypes([...policyTypes]);
    } else {
      policyTypes.splice(indexOfId, 1);
      return setCurrentPolicyTypes([...policyTypes]);
    }
  };

  const debouncedUpdatePolicyType = useDebouncedCallback(policyType => {
    updatePolicyType(policyType);
  }, 200);

  const checkForm = () => {
    const errors = {} as any;
    if (currentPolicyTypes.length === 0) {
      errors.policyType = true;
      setFormErrors(errors);
      return errors;
    } else {
      return {};
    }
  };

  const clearAum = () => {
    setAumRangeFrom('');
    setAumRangeTo('');
  };

  const onChangeChargeType = (index: number, value: number) => {
    const newCharge = {
      ...chargeData,
      charge_type: value
    };
    updateChargeData(newCharge);
    setChargeType(value);
  };

  const getRangeString = (from: string, to: string) => {
    if (!from && !to) {
      return 'N/A';
    }
    if (from && !to) {
      return `${formatCurrency(from)}+`;
    }
    return `${from ? formatCurrency(from) : '£0.00'} - ${to}`;
  };

  useEffect(() => {
    setAumRangeString(getRangeString(aumRangeFrom, aumRangeTo));
  }, [aumRangeFrom, aumRangeTo]);

  return (
    <div key={`ChargeTyp--${index}`}>
      <div style={{ marginTop: '1.5rem' }}>
        <span style={{ fontWeight: 'bold' }}>Policy Types: </span>
      </div>
      {
        <PolicyTypeRadio
          selectedPolicies={currentPolicyTypes}
          onChange={debouncedUpdatePolicyType}
          error={formErrors.policyType}
          isFirmAdmin={isFirmAdmin}
        />
      }
      <div
        style={{
          marginTop: '1.5rem',
          display: 'flex',
          alignItems: 'end',
          gap: '5px'
        }}
      >
        <span style={{ fontWeight: 'bold' }}>Charge Type</span>
        <Tooltip
          text={
            <div>
              <p>
                {
                  'A `Stepped Charge` is when the total AUM falls within a charge band and the total AUM is charged at the same rate. e.g. AUM of £250,000 that falls within a 0.30% charge band of £100,000 to £300,000, then the whole £250,000 is charged at 0.30% (£750).'
                }
              </p>
              {
                'A `Tiered Charge` is when a charge applies only to the AUM that falls within a charge band. e.g. AUM of £250,000 within a charge band of 0.30% on £0 - £100,000 and 0.25% on £100,001 to £300,000, is charged at 0.30% (£300) on the first £100,000 and 0.25% (£375) on the remaining £150,000 (overall charge of £675).'
              }
              <p>
                {
                  'A `Fixed Charge` is a fixed rate (% or £) that applies to total AUM. e.g. Fixed drawdown fee of £150 per annum.'
                }
              </p>
            </div>
          }
        />
      </div>
      <ChargeTypeRadio
        chargeType={chargeType}
        chargeTypes={chargeTypes}
        onChange={onChangeChargeType}
        index={index}
        isFirmAdmin={isFirmAdmin}
      />

      <FamilyLinkRadio
        isFamilyLinked={isFamilyLinked}
        setIsFamilyLinked={setIsFamilyLinked}
        isFirmAdmin={isFirmAdmin}
      />

      <>
        <div
          style={{
            marginTop: '1.5rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'end',
            gap: '5px'
          }}
        >
          <span style={{ fontWeight: 'bold' }}>AUM Range</span>
          <Tooltip text="Insert range if this platform charge only applies when the total AUM on the platform is within this range." />
        </div>
        <div className="aumRange">
          {isFirmAdmin ? (
            <>
              <div
                style={{
                  width: '100px',
                  marginRight: '5rem',
                  marginBottom: '1rem'
                }}
              >
                <TextInput
                  label="From"
                  onChange={(e: string) => setAumRangeFrom(e)}
                  value={aumRangeFrom}
                  style={{
                    marginTop: '0.3rem',
                    marginBottom: '0.25rem',
                    width: 'max-content'
                  }}
                  formatter={formatCurrency}
                />
              </div>
              <div style={{ width: '100px' }}>
                <TextInput
                  label="To"
                  onChange={(e: string) => setAumRangeTo(e)}
                  value={aumRangeTo}
                  style={{
                    marginTop: '0.3rem',
                    marginBottom: '0.25rem',
                    width: 'max-content'
                  }}
                  formatter={formatCurrency}
                />
              </div>
              <div>
                <Button onClick={clearAum} small color="primary">
                  Clear
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{
                marginTop: '-0.5rem',
                marginBottom: '1rem'
              }}
            >
              {aumRangeString}
            </div>
          )}
        </div>
      </>

      {chargeTypes[chargeType as keyof typeof chargeTypes] === 'Stepped' && (
        <SteppedCharge
          currentPolicyTypes={currentPolicyTypes}
          steppedChargeData={chargeData}
          updateCharge={updateCharge}
          index={index}
          isFamilyLinked={isFamilyLinked}
          checkForm={checkForm}
          selectedPlatformType={selectedPlatformType}
          selectedProviderType={selectedProviderType}
          platformIndex={platformIndex}
          aumRangeFrom={aumRangeFrom}
          aumRangeTo={aumRangeTo}
          isFirmAdmin={isFirmAdmin}
          getRangeString={getRangeString}
        />
      )}
      {chargeTypes[chargeType as keyof typeof chargeTypes] === 'Tiered' && (
        <TieredCharge
          currentPolicyTypes={currentPolicyTypes}
          tieredChargeData={chargeData}
          updateCharge={updateCharge}
          index={index}
          isFamilyLinked={isFamilyLinked}
          checkForm={checkForm}
          selectedPlatformType={selectedPlatformType}
          selectedProviderType={selectedProviderType}
          platformIndex={platformIndex}
          aumRangeFrom={aumRangeFrom}
          aumRangeTo={aumRangeTo}
          isFirmAdmin={isFirmAdmin}
          getRangeString={getRangeString}
        />
      )}
      {chargeTypes[chargeType as keyof typeof chargeTypes] === 'Fixed' && (
        <FixedCharge
          fixedChargeData={chargeData}
          currentPolicyTypes={currentPolicyTypes}
          updateCharge={updateCharge}
          index={index}
          isFamilyLinked={isFamilyLinked}
          checkForm={checkForm}
          selectedPlatformType={selectedPlatformType}
          selectedProviderType={selectedProviderType}
          platformIndex={platformIndex}
          aumRangeFrom={aumRangeFrom}
          aumRangeTo={aumRangeTo}
          applicationTypes={applicationTypes}
          isFirmAdmin={isFirmAdmin}
        />
      )}
    </div>
  );
};

export default ChargeSet;
